import React, { useCallback, useEffect, useRef, useState } from 'react';
import { MouseEventHandler } from 'react';
import WaveSurfer from 'wavesurfer.js';
import cn from 'classnames';
import Slider from '@material-ui/core/Slider';
import { useRouter } from 'next/router';
import Icon from 'components/common/Icon';
import useStore from 'store/timeline';
import useGlobalStore from 'store/global';
import {
  AUDIO_TMP_URL,
  DeviceInfo,
  increaseViewCount,
  isNumber,
  loadImageHandleError,
  secondsToHms,
  useDevice,
} from 'common/utils';
import {
  MembershipPackageName,
  PostParentKey,
  PostSource,
  PostType,
  User,
} from 'common/interfaces/api';

import styles from './AudioPlayer.module.scss';
import { useMemo } from 'react';
import { WaveSurferParams } from 'wavesurfer.js/types/params';
import PlayerRateOptions from '../PlayerRateOptions';
import Button from 'components/common/Button';
import { isGoldMember } from 'common/utils/membership';
import { parentKeyToScreenName, ScreenName } from 'common/utils/pp_tracking';
import {
  addClickPostEvent,
  checkIsCreatorPaidPost,
  exCreatorPostLockMessage,
} from 'common/utils/post';
import { MediaType } from 'service/media';
import { track } from '@amplitude/analytics-browser';

const formWaveSurferOptions = (ref: HTMLElement): WaveSurferParams => ({
  container: ref,
  backgroundColor: '#0ABC71',
  waveColor: 'rgba(255, 255, 255, 0.33)',
  progressColor: '#ffffff',
  cursorColor: 'transparent',
  barWidth: 4,
  barGap: 0,
  height: 100,
  hideScrollbar: true,
  normalize: true,
  backend: 'MediaElement',
});

export type Props = {
  id: number;
  trackTitle: string;
  postAuthor: User;
  url: string;
  // use this to diffrentiate audio player with same ID in different parnt components
  parent?: string;
  isGlobal?: boolean;
  titleEditable?: boolean;
  editableTitle?: string;
  onChangeTitle?: (newTitle: string) => void;
  isLocked?: boolean;
  noPadding?: boolean;
  spectrum?: number[];
  duration?: number;
  isPostDetail?: boolean;
  postType?: PostType;
  thumbnailUrl?: string;
  canSeeFreeMinutes?: boolean;
  freeMinutes?: number;
  freeUrl?: string;
  postSource?: PostSource;
};

function AudioPlayer({
  id,
  url,
  trackTitle,
  postAuthor,
  parent = 'timeline',
  isGlobal = false,
  titleEditable = false,
  editableTitle,
  onChangeTitle,
  isLocked = false,
  spectrum,
  duration = 0,
  isPostDetail = false,
  postType,
  thumbnailUrl,
  canSeeFreeMinutes,
  freeMinutes,
  freeUrl,
  postSource,
}: Props): JSX.Element {
  const router = useRouter();
  const deviceInfo: DeviceInfo = useDevice();
  const isMobile = !deviceInfo.isDesktop;
  const [activeAudioPlayer, setActiveAudioPlayer] = useStore((state) => [
    state.activeAudioPlayer,
    state.setActiveAudioPlayer,
  ]);
  const currentAuth = useGlobalStore((state) => state.currentAuth);
  const currentUser = useStore((state) => state.currentUser);
  const activeVideoPlayer = useStore((state) => state.activeVideoPlayer);
  const showLogin = useGlobalStore((state) => state.showLogin);
  const setShowMembershipPayDialog = useGlobalStore(
    (state) => state.setShowMembershipPayDialog
  );
  const showPaymentDialog = useGlobalStore((state) => state.showPaymentDialog);
  const playerId = parent + id.toString();
  const isActive = playerId === activeAudioPlayer?.id;
  const isActiveRef = useRef(isActive);
  const setSnackbarMessage = useGlobalStore(
    (state) => state.setSnackbarMessage
  );
  const [playedFreeTimes, setPlayedFreeTimes] = useState(0);
  const [remainingFreeSeconds, setRemainingFreeSeconds] = useState(0);
  const [playbackRate, setPlaybackRate] = useState(1);
  const postUrl = `/${postAuthor.username}/posts/${id}`;
  const waveformRef = useRef<HTMLDivElement>(null);
  const wavesurfer = useRef<WaveSurfer>(null);
  const [playing, setPlaying] = useState(false);
  const [hasEnded, setHasEnded] = useState(false);
  const [isLoading, setLoading] = useState(true);
  const [currentTime, setCurrentTime] = useState(0);
  const [isHidden, setHidden] = useState(false);
  const [volume, setVolume] = useState(1);
  const [timebarVal, setTimebarVal] = useState(0);
  const [lastClickTime, setLastClickTime] = useState(-1);
  useEffect(() => {
    setPlayedFreeTimes(0);
  }, [id]);

  const isMiniModeActive = isActive && (isHidden || isGlobal);
  const audioPlayerRef = useRef<HTMLDivElement>(null);
  const intersectionCb: IntersectionObserverCallback = useCallback(
    (entries) => {
      const target = entries[0];
      if (target) {
        if (target.isIntersecting) {
          // audio player showing
          setHidden(false);
        } else {
          // audio player ofsscreen
          setHidden(true);
        }
      }
    },
    []
  );

  // Timebar
  const handleTimebarChange = async (
    e: React.ChangeEvent<HTMLInputElement>
  ) => {
    e.stopPropagation();
    if (titleEditable) return;
    if (!currentAuth) {
      showLogin(router.pathname === '/' ? { redirectUrl: postUrl } : {});
      return;
    }
    if (isLocked) return;
    if (!isPostDetail && !isActive) {
      addClickPostEvent({
        postId: id,
        screen: parentKeyToScreenName(parent) as ScreenName,
        postSource: postSource,
        clickPosition: 'media_view',
        postType: postType,
        mediaType: MediaType.Audio,
      });
      await router.push(postUrl);
      return;
    }
    if (!wavesurfer.current) return;
    const newTimebarVal = Number(e.target.value);
    if (isNaN(newTimebarVal) || newTimebarVal < 0 || newTimebarVal > 1) return;
    wavesurfer.current.seekTo(newTimebarVal);
    setTimebarVal(newTimebarVal);
    if (newTimebarVal === 1) {
      setPlaying(false);
      setHasEnded(true);
      void wavesurfer.current.pause();
    } else {
      setPlaying(true);
      setHasEnded(false);
      void wavesurfer.current.play();
    }
  };

  const handleTrialClick: MouseEventHandler = (e) => {
    e.stopPropagation();
    if (postType === PostType.Gold && !isGoldMember(currentUser)) {
      setShowMembershipPayDialog({
        open: true,
        showMembershipStatus: false,
        initPackage: MembershipPackageName.Platinum,
        screen: ScreenName.PostDetail,
      });
      return;
    }
    if (!postAuthor) return;
    // in case of value is undefined, still can open payment dialog
    if (
      postAuthor.is_open_for_subscription === false &&
      checkIsCreatorPaidPost(postType)
    ) {
      setSnackbarMessage({
        type: 'error',
        text: exCreatorPostLockMessage(postType),
      });
      return;
    }
    showPaymentDialog({ creator: postAuthor });
  };

  useEffect(() => {
    if ((isLocked && !canSeeFreeMinutes) || isGlobal) return;
    const ref = audioPlayerRef.current;
    const options = {
      root: null, // window by default
      rootMargin: isMobile ? '10px' : thumbnailUrl ? '240px' : '10px',
      threshold: 0.2,
    };
    // Create observer
    const observer = new IntersectionObserver(intersectionCb, options);
    const scrollCheck = setTimeout(() => {
      // observe the audio player
      if (ref) {
        observer.observe(ref);
      }
    }, 500);

    // clean up on willUnMount
    return () => {
      if (ref) {
        observer.unobserve(ref);
      }
      scrollCheck && clearTimeout(scrollCheck);
    };
    // Note: for some reasone audioPlayer.current needs to be in the dependency array
    // otherwise initial target.isIntersecting will always be false
  }, [
    intersectionCb,
    isLocked,
    isGlobal,
    thumbnailUrl,
    isMobile,
    canSeeFreeMinutes,
  ]);

  useEffect(() => {
    if (!canSeeFreeMinutes) return;
    const rmFreeSecs = Math.round(
      Math.max(0, Math.min(freeMinutes * 60, duration) - currentTime)
    );

    setRemainingFreeSeconds(rmFreeSecs);
    if (rmFreeSecs === 1) {
      setPlayedFreeTimes(0);
      wavesurfer.current.seekTo(0);
      wavesurfer.current.pause();
      setPlaying(false);
      showPaymentDialog({ creator: postAuthor });
    }
  }, [
    canSeeFreeMinutes,
    currentTime,
    duration,
    freeMinutes,
    postAuthor,
    showPaymentDialog,
  ]);

  const currentUrl = useMemo(() => {
    if (!isPostDetail) return AUDIO_TMP_URL;
    const newUrl =
      canSeeFreeMinutes && freeUrl ? freeUrl : isLocked ? AUDIO_TMP_URL : url;
    return newUrl || AUDIO_TMP_URL;
  }, [canSeeFreeMinutes, freeUrl, isLocked, isPostDetail, url]);

  // create new WaveSurfer instance
  // On component mount and when url changes
  useEffect(() => {
    try {
      if (!currentUrl) return;
      setPlaying(false);
      const options = formWaveSurferOptions(waveformRef.current);
      wavesurfer.current = WaveSurfer.create(options);
      wavesurfer.current.load(currentUrl, spectrum);
      if (currentUrl === AUDIO_TMP_URL) {
        setLoading(false);
      }
      wavesurfer.current.on('ready', () => {
        setLoading(false);
        // make sure object stillavailable when file loaded
        if (wavesurfer.current) {
          if (!isLocked && playedFreeTimes > 0) {
            wavesurfer.current.setCurrentTime(
              Math.min(playedFreeTimes, wavesurfer.current.getDuration() - 1)
            );
            void wavesurfer.current.play();
            setPlaying(true);
            setPlayedFreeTimes(0);
          }
          wavesurfer.current.setVolume(1);
        }
      });
      wavesurfer.current.on('finish', () => {
        setHasEnded(true);
        setPlaying(false);
      });
      wavesurfer.current.on('audioprocess', () => {
        let currentTime = wavesurfer.current.getCurrentTime();
        if (currentTime < 0) currentTime = 0;
        setTimebarVal(wavesurfer.current.getCurrentTime() / duration);
        setCurrentTime(currentTime);
        if (canSeeFreeMinutes) {
          setPlayedFreeTimes(currentTime);
        }
      });
      wavesurfer.current.on('seek', () => {
        let currentTime = wavesurfer.current.getCurrentTime();
        if (currentTime < 0) currentTime = 0;
        setTimebarVal(wavesurfer.current.getCurrentTime() / duration);
        setCurrentTime(currentTime);
      });

      wavesurfer.current.on('volume', (volume: number) => {
        setVolume(volume);
      });
    } catch (error) {
      console.error(error);
    }
    // Removes events, elements and disconnects Web Audio nodes.
    // when component unmount
    return () => wavesurfer.current.destroy();
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [currentUrl, spectrum]);

  const draw = () => {
    try {
      if (wavesurfer.current) {
        // eslint-disable-next-line @typescript-eslint/no-unsafe-call
        wavesurfer.current.drawBuffer();
        // eslint-disable-next-line @typescript-eslint/no-explicit-any
        const backend: any = wavesurfer.current.backend;
        // redraw the progress when player changes form
        // eslint-disable-next-line
        wavesurfer.current.drawer.progress(backend.getPlayedPercents());
      }
      // eslint-disable-next-line no-empty
    } catch (error) {}
  };

  // redraw the bars when the player changes form
  // TODO: FIXME: using internal methods of wavesurfer.js
  // may not work on newer versions
  useEffect(() => {
    draw();
  }, [isHidden]);

  // Stop playing when video starts playing with sound
  useEffect(() => {
    if (activeVideoPlayer && activeVideoPlayer.muted === false) {
      if (wavesurfer.current && wavesurfer.current.isPlaying) {
        void wavesurfer.current.pause();
        setPlaying(false);
      }
    }
  }, [activeVideoPlayer]);

  // Stop playing when other audio sources are playing
  useEffect(() => {
    if (!isActive && wavesurfer.current && wavesurfer.current.isPlaying) {
      void wavesurfer.current.pause();
      setPlaying(false);
    }
    isActiveRef.current = isActive;
  }, [isActive]);

  // Set current audio as active if it is playing
  useEffect(() => {
    if (playing) {
      setActiveAudioPlayer(playerId, true);
    } else {
      if (isActiveRef.current) {
        setActiveAudioPlayer(playerId, false);
      }
    }
  }, [playing, playerId, setActiveAudioPlayer]);

  const handlePlayPause: MouseEventHandler = async (e) => {
    if (titleEditable) return;
    if (!isPostDetail && !isActive) {
      addClickPostEvent({
        postId: id,
        screen: parentKeyToScreenName(parent) as ScreenName,
        postSource: postSource,
        clickPosition: 'media_view',
        postType: postType,
        mediaType: MediaType.Audio,
      });
      await router.push(postUrl);
      return;
    }
    if (!currentAuth) {
      showLogin({});
      return;
    }
    if (!wavesurfer.current) return;
    if (!isLocked || canSeeFreeMinutes) {
      if (playing) {
        void wavesurfer.current.pause();
        setPlaying(false);
      } else {
        void wavesurfer.current.play();
        setPlaying(true);
        track('Start Play Audio');
        increaseViewCount(id);
      }
      setHasEnded(false);
    } else {
      handleTrialClick(e);
    }
  };

  // Rewind and fast forward with
  // - positivie skipTime means fast forward
  // - negative skipTime means rewind
  const handleTimeSkip = async (skipTime: number) => {
    if (titleEditable) return;
    if (!currentAuth) {
      showLogin(router.pathname === '/' ? { redirectUrl: postUrl } : {});
      return;
    }

    if (!isPostDetail && !isActive) {
      addClickPostEvent({
        postId: id,
        screen: parentKeyToScreenName(parent) as ScreenName,
        postSource: postSource,
        clickPosition: 'media_view',
        postType: postType,
        mediaType: MediaType.Audio,
      });
      await router.push(postUrl);
      return;
    }
    if (isLocked && !canSeeFreeMinutes) return;
    if (!wavesurfer?.current) return;
    if (skipTime < 0) {
      increaseViewCount(id);
    }

    let newTime = wavesurfer.current.getCurrentTime() + skipTime;
    if (newTime > wavesurfer.current.getDuration())
      newTime = wavesurfer.current.getDuration() - 1;
    if (newTime < 0) newTime = 0;
    setCurrentTime(newTime);
    setTimebarVal(newTime / duration);
    wavesurfer.current.setCurrentTime(newTime);
  };

  const mediaControlBtn = useMemo(() => {
    if (hasEnded) {
      return <Icon name="replay-2" width={42} height={42} />;
    } else {
      return (
        <Icon name="pause-2" width={42} height={42} hasOn isOn={!playing} />
      );
    }
  }, [hasEnded, playing]);

  const handleCloseBtn = () => {
    setActiveAudioPlayer(null, null);
  };

  // Volume
  const handleVolumeChange = (e, newVolume: number | number[]) => {
    if (!isNumber(newVolume) || Array.isArray(newVolume)) {
      newVolume = 0.5;
    }
    wavesurfer.current.setVolume(newVolume);
  };
  const handleSpeakerClick = () => {
    if (!wavesurfer.current) return;
    wavesurfer.current.toggleMute();
  };

  const handleChangePlaybackRate = (newRate: number) => {
    if (!wavesurfer?.current) return;
    setPlaybackRate(newRate);
    wavesurfer.current.setPlaybackRate(newRate);
  };

  return (
    <div style={{ minHeight: isGlobal ? '0' : '115px' }}>
      {!isGlobal && <div className={styles.invisible} ref={audioPlayerRef} />}
      {!isMiniModeActive && (
        <ThumbnailImageFull
          thumbnailUrl={thumbnailUrl}
          postUrl={postUrl}
          isPostDetail={isPostDetail}
          parent={parent}
          postSource={postSource}
          trackTitle={trackTitle}
          postId={id}
          postType={postType}
        />
      )}
      <div
        className={cn(styles.audioPlayerWrapper, {
          [styles.floating]: isMiniModeActive,
          [styles.hasThumbnail]: !!thumbnailUrl,
        })}
      >
        {isMiniModeActive && (
          <div className={styles.closeBtn} onClick={handleCloseBtn}>
            <Icon name="close-2" width={24} height={24} />
          </div>
        )}
        {isMiniModeActive && <ThumbnailImageMini thumbnailUrl={thumbnailUrl} />}
        <div className={styles.audioBody}>
          <div className={styles.trackInfo}>
            <div className={styles.trackAuthor}>{postAuthor?.name}</div>
            <div className={styles.trackTitle}>
              {titleEditable ? (
                <input
                  autoFocus
                  type="text"
                  placeholder="タイトルをつける"
                  className={styles.titleInput}
                  value={editableTitle}
                  onChange={(e) => onChangeTitle(e.target.value)}
                />
              ) : (
                trackTitle
              )}
            </div>
          </div>
          <div className={styles.bottom}>
            {isLoading && (
              <div className={styles.loading}>
                <Icon name="loading-anim-2" width={30} height={30} />
              </div>
            )}
            <div className={styles.center}>
              <div
                className={styles.waveformContainer}
                style={canSeeFreeMinutes ? { visibility: 'hidden' } : {}}
              >
                <div ref={waveformRef} />
              </div>
              <div className={styles.timebar}>
                <input
                  key={`audio-time-slider-${id}`}
                  type="range"
                  min={0}
                  max={1}
                  step={0.025}
                  value={timebarVal}
                  style={{
                    background: `linear-gradient(to right, #fff 0%, #fff ${
                      timebarVal * 100
                    }%, #ccc ${timebarVal * 100}%, #ccc 100%)`,
                  }}
                  onChange={handleTimebarChange}
                  className={styles.sliderTime}
                  onMouseDown={() => {
                    setLastClickTime(timebarVal);
                  }}
                  onMouseUp={() => {
                    // call event increase view count
                    if (lastClickTime > timebarVal) {
                      increaseViewCount(id);
                    }
                    setLastClickTime(-1);
                  }}
                />
              </div>
            </div>
            <div className={styles.controlBtns}>
              <div className={styles.controlTop}>
                <div className={styles.skipBtn}>
                  <Icon
                    name="play-rewind"
                    height={35}
                    width={35}
                    onClick={() => handleTimeSkip(-15)}
                  />
                </div>
                <div onClick={handlePlayPause} style={{ cursor: 'pointer' }}>
                  {mediaControlBtn}
                </div>
                <div className={styles.skipBtn}>
                  <Icon
                    name="play-forward"
                    height={35}
                    width={35}
                    onClick={() => handleTimeSkip(15)}
                  />
                </div>
              </div>
              <div className={styles.controlBottom}>
                <div className={styles.speedCtrl}>
                  <div className={styles.speedCtrlOptions}>
                    <PlayerRateOptions
                      setPlaybackRate={handleChangePlaybackRate}
                      isWhite
                    />
                  </div>
                  <div className={styles.speedBtn}>{playbackRate}x</div>
                </div>
                <div className={styles.progress}>
                  {isMiniModeActive
                    ? secondsToHms(currentTime)
                    : `${secondsToHms(currentTime)} / ${secondsToHms(
                        duration || wavesurfer.current?.getDuration() || 0
                      )}`}
                </div>
                <div className={styles.volumeCtrl}>
                  <div
                    className={styles.speakerIcon}
                    onClick={handleSpeakerClick}
                  >
                    <Icon
                      name="speaker"
                      width={26}
                      height={26}
                      hasOn
                      isOn={volume > 0.01}
                    />
                  </div>
                  {!isMobile &&
                    (isLoading || (
                      <Slider
                        key={`audio-slider-${id}`}
                        defaultValue={1}
                        // waveSurfer recognize value of `0` same as `1`
                        //  so we need to set some zero-ish value for silence
                        min={0.01}
                        max={1}
                        step={0.025}
                        onChange={handleVolumeChange}
                        className={styles.volumeSlider}
                      />
                    ))}
                </div>
              </div>
            </div>
          </div>
        </div>
      </div>
      {canSeeFreeMinutes && isPostDetail && !isMiniModeActive && (
        <div className={styles.freeTimeSection}>
          <span className={styles.time}>
            {secondsToHms(Math.max(remainingFreeSeconds, 0))}
          </span>
          <span>残りの無料時間</span>
          <Button
            text="プライム登録を無料体験"
            alternateColor
            onClick={(e) => {
              handleTrialClick(e);
            }}
          />
        </div>
      )}
    </div>
  );
}

const ThumbnailImageMini = ({ thumbnailUrl }: { thumbnailUrl: string }) => {
  const linkThumbnailRef = useRef<HTMLImageElement>(null);
  if (!thumbnailUrl) return <></>;
  return (
    <div className={styles.thumbnailImageMini}>
      <img
        loading="lazy"
        src={thumbnailUrl}
        ref={linkThumbnailRef}
        onContextMenu={(e) => e.preventDefault()}
        onError={() =>
          loadImageHandleError(thumbnailUrl, linkThumbnailRef, () => {
            if (!linkThumbnailRef.current) return;
            linkThumbnailRef.current.style.display = 'none';
          })
        }
      />
    </div>
  );
};

const ThumbnailImageFull = ({
  thumbnailUrl,
  postUrl,
  isPostDetail,
  postId,
  parent,
  postSource,
  trackTitle,
  postType,
}: {
  thumbnailUrl: string;
  postUrl: string;
  isPostDetail: boolean;
  postId: number;
  parent: PostParentKey | string;
  postSource: PostSource;
  trackTitle: string;
  postType: PostType;
}) => {
  const linkThumbnailRef = useRef<HTMLImageElement>(null);
  const router = useRouter();
  const currentAuth = useGlobalStore((state) => state.currentAuth);
  const showLogin = useGlobalStore((state) => state.showLogin);

  if (!thumbnailUrl) return <></>;
  return (
    <div className={styles.thumbnailImageFull}>
      <img
        onClick={() => {
          if (!currentAuth) {
            showLogin(router.pathname === '/' ? { redirectUrl: postUrl } : {});
            return;
          }
          if (!isPostDetail) {
            void router.push(postUrl);
            addClickPostEvent({
              postId: postId,
              screen: parentKeyToScreenName(parent) as ScreenName,
              postSource: postSource,
              clickPosition: 'media_view',
              postType: postType,
              mediaType: MediaType.Audio,
            });
          }
        }}
        loading="lazy"
        alt={trackTitle}
        src={thumbnailUrl}
        ref={linkThumbnailRef}
        onContextMenu={(e) => e.preventDefault()}
        onError={() =>
          loadImageHandleError(thumbnailUrl, linkThumbnailRef, () => {
            if (!linkThumbnailRef.current) return;
            linkThumbnailRef.current.style.display = 'none';
          })
        }
      />
    </div>
  );
};

const MemoizedAudioPlayer = React.memo(AudioPlayer);
export default MemoizedAudioPlayer;
